import { useCallback } from 'react';
import { useUserStore } from '../store/useUserStore';
import { trackPostHogEvent } from '../lib/posthog';
import { addSentryBreadcrumb } from '../lib/sentry';

export type BlockReason = 'inactive' | 'no_credits' | null;

interface CreditCheckResult {
  allowed: boolean;
  reason: BlockReason;
  credits: number;
  isActive: boolean;
}

/**
 * Hook for checking if user can perform credit-consuming actions
 * Provides both validation and analytics tracking
 */
export function useCreditCheck() {
  const credits = useUserStore((state) => state.credits);
  const isActive = useUserStore((state) => state.isActive);
  const isSuperAdmin = useUserStore((state) => state.isSuperAdmin);

  const checkCanGenerate = useCallback((): CreditCheckResult => {
    // Superadmin always has access
    if (isSuperAdmin) {
      return { allowed: true, reason: null, credits, isActive: true };
    }

    // Check if account is active
    if (!isActive) {
      trackPostHogEvent('generation_blocked', {
        reason: 'inactive_account',
        credits,
      });
      addSentryBreadcrumb('Generation blocked - inactive account', {
        credits,
        level: 'warn',
      });
      return { allowed: false, reason: 'inactive', credits, isActive };
    }

    // Check if user has credits
    if (credits < 1) {
      trackPostHogEvent('generation_blocked', {
        reason: 'insufficient_credits',
        credits,
      });
      addSentryBreadcrumb('Generation blocked - no credits', {
        credits,
        level: 'warn',
      });
      return { allowed: false, reason: 'no_credits', credits, isActive };
    }

    return { allowed: true, reason: null, credits, isActive };
  }, [credits, isActive, isSuperAdmin]);

  const checkCanAccessTool = useCallback(
    (toolId: string): boolean => {
      const store = useUserStore.getState();
      return store.canAccessTool(toolId);
    },
    []
  );

  return {
    checkCanGenerate,
    checkCanAccessTool,
    credits,
    isActive,
    isSuperAdmin,
  };
}
